import {
  type SavedCypherTree,
  type UpdateSavedCypherTreeApiArg,
  buildSavedCypherTreeOrderedItemsFromFlatTreeItems,
  useUpdateSavedCypherTreeMutation,
} from '@query/services/api';
import type { FlatTreeItems } from '@query/types/saved-cypher';
import { useCallback } from 'react';

type UpdateSavedCypherTreeFnArg = Pick<UpdateSavedCypherTreeApiArg, 'id'> & {
  flatItems: FlatTreeItems;
};
type UpdateSavedCypherTreeFn = (arg: UpdateSavedCypherTreeFnArg) => Promise<SavedCypherTree>;

/**
 * Custom hook that encapsulates the logic to rebuild a saved cypher tree structures' orderedItem array from
 * an array of flat items from the presentation layer.
 *
 * @returns The updated tree structure.
 */
export const useCommonPatchSavedCypherTreeMutation = () => {
  const [updateSavedCypherTree, updateTreeSavedCypherTreeRes] = useUpdateSavedCypherTreeMutation();

  const updateSavedCypherTreeInternal: UpdateSavedCypherTreeFn = useCallback(
    async (arg) => {
      const { id, flatItems: flattenedItems } = arg;
      const orderedItems = buildSavedCypherTreeOrderedItemsFromFlatTreeItems(flattenedItems);

      const res = await updateSavedCypherTree({
        id,
        savedCypherTree: { orderedItems },
      }).unwrap();
      return res;
    },
    [updateSavedCypherTree],
  );

  const res = [
    updateSavedCypherTreeInternal,
    {
      isLoading: updateTreeSavedCypherTreeRes.isLoading,
    },
  ] as const;

  return res;
};
